import { type HTMLAttributes, type ReactElement, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Eyebrow } from "./Eyebrow";
import { type H2Size } from "./H2Size";
import { type H2Variant } from "./H2Variant";

const SIZE_CLASSES: Record<H2Size, string> = {
    sm: "text-sm",
    md: "text-lg",
    lg: "text-xl",
};

interface Props extends HTMLAttributes<HTMLHeadingElement> {
    children: ReactNode;
    size?: H2Size;
    /**
     * `label` renders the heading as an editorial mono eyebrow (uppercase,
     * tracked, muted) for grouping rows inside a surface. `size` is ignored
     * for this variant.
     */
    variant?: H2Variant;
    /**
     * Optional mono eyebrow rendered above the heading (stage / step group
     * labels). Wrapped with the heading in one block element.
     */
    eyebrow?: ReactNode;
}

/**
 * The platform's section / subheading. Always sans — the serif display face is
 * reserved for {@link H1}; subheadings never use it. Never write a raw `<h2>`.
 *
 * ```tsx
 * <H2>{t("accounts.contacts")}</H2>
 * <H2 size="sm">{t("claims.timeline")}</H2>
 * <H2 variant="label">{t("vehicles.state.current")}</H2>
 * ```
 */
export function H2({ children, size = "md", variant = "default", eyebrow, className, ...rest }: Props): ReactElement {
    const heading = (
        <h2
            className={cn(
                variant === "label"
                    ? "font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground"
                    : cn("font-sans font-semibold tracking-tight text-foreground", SIZE_CLASSES[size]),
                className,
            )}
            {...rest}
        >
            {children}
        </h2>
    );

    if (eyebrow === undefined || eyebrow === null || eyebrow === false) {
        return heading;
    }

    return (
        <div className="min-w-0">
            <Eyebrow className="mb-1">{eyebrow}</Eyebrow>
            {heading}
        </div>
    );
}
